/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Fri May 10 2024
 **/

import styled from "@emotion/styled";
import { createContext, useContext, useEffect } from "react";

import { Column, Row } from "../../../Layouts";
import useModal from "../useModal";
import { Button } from "../../../Inputs";
import { Typography } from "../../../Typography";

interface PopupProps {
	modalID: string;
	width?: 440 | 480 | 600;
	fitContent?: boolean;
	children: React.ReactNode;
}

interface PopupTitleProps {
	children: React.ReactNode;
	titleTypography?: DesignStuff.Typography.Props;
}

interface PopupContentsProps {
	children: React.ReactNode;
}

interface PopupBottomProps {
	confirmText?: React.ReactNode;
	cancelText?: React.ReactNode;
	stretch?: boolean;
	onCancel?: (ev: React.MouseEvent<HTMLButtonElement>) => void;
	onConfirm?: (ev: React.MouseEvent<HTMLButtonElement> | KeyboardEvent, hide: () => void) => void;
}

const PopupContext = createContext<Omit<PopupProps, "children"> | undefined>(undefined);

const ModalPopup = (props: PopupProps) => {
	return (
		<PopupContext.Provider value={props}>
			<StyledPopup width={props.width ?? 440} fitContent={Boolean(props.fitContent)}>
				{props.children}
			</StyledPopup>
		</PopupContext.Provider>
	);
};

const Title = (props: PopupTitleProps) => {
	const popup = useContext(PopupContext);
	if (!popup) throw Error("ModalPopup Context가 없습니다.");

	return (
		<Column css={{ width: "100%", marginBottom: "20px" }}>
			{typeof props.children === "string" ? (
				<Typography {...props.titleTypography} typoSize={props.titleTypography?.typoSize ?? "Head6"}>
					{props.children}
				</Typography>
			) : (
				props.children
			)}
		</Column>
	);
};

const Contents = (props: PopupContentsProps) => {
	const popup = useContext(PopupContext);
	if (!popup) throw Error("ModalPopup Context가 없습니다.");

	return <StyledPopupContents width={popup.width ?? 440}>{props.children}</StyledPopupContents>;
};

const Bottom = (props: PopupBottomProps) => {
	const popup = useContext(PopupContext);
	const modalActions = useModal();

	const hide = () => {
		if (!popup?.modalID) return;
		modalActions.hide(popup.modalID);
	};

	const cancel = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (!popup) throw Error("ModalPopup Context가 없습니다.");
		props.onCancel?.(ev);
		hide();
	};

	const confirm = (ev: React.MouseEvent<HTMLButtonElement> | KeyboardEvent) => {
		props.onConfirm?.(ev, hide);
	};

	const buttonCSS = {
		width: props.stretch ? "100%" : "auto",
		minWidth: "120px"
	};

	const confirmByEnter = (ev: KeyboardEvent) => {
		if (!props.confirmText) return;
		if (ev.key === "Enter") {
			confirm(ev);
		}
	};

	useEffect(() => {
		document.addEventListener("keydown", confirmByEnter);
		return () => {
			document.removeEventListener("keydown", confirmByEnter);
		};
	}, [props.onConfirm]);

	if (!popup) throw Error("ModalPopup Context가 없습니다.");

	return (
		<StyledPopupBottom gap={8} horizonAlign="center">
			{props.cancelText && (
				<Button size="large" css={buttonCSS} filled="secondary" onClick={cancel}>
					{props.cancelText}
				</Button>
			)}
			{props.confirmText && (
				<Button autoFocus css={buttonCSS} onClick={confirm}>
					{props.confirmText}
				</Button>
			)}
		</StyledPopupBottom>
	);
};

Title.displayName = "ModalPopupTitle";
ModalPopup.Title = Title;

Contents.displayName = "ModalPopupContents";
ModalPopup.Contents = Contents;

Bottom.displayName = "ModalPopupBottomBar";
ModalPopup.Bottom = Bottom;

export default ModalPopup;

const getPopupHeight = (width: 440 | 480 | 600, fitContent: boolean) => {
	if (width === 600) {
		return fitContent ? "auto" : "640px";
	}
	return "auto";
};

const getPopupMaxHeight = (width: 440 | 480 | 600, fitContent: boolean) => {
	if (fitContent) return "auto";
	return width === 440 ? "640px" : "auto";
};

const getPopupMinHeight = (width: 440 | 480 | 600, fitContent: boolean) => {
	if (fitContent) return "auto";
	return width === 440 ? "240px" : "auto";
};

const StyledPopup = styled(Column)<{ width: 440 | 480 | 600; fitContent: boolean }>`
	border-radius: 12px;
	background: ${({ theme }) => theme.colors.neutral_00_100};
	box-shadow: 0px 12px 20px 0px rgba(0, 0, 0, 0.2);
	padding: 28px 32px 4px 32px;
	width: ${({ width }) => width}px;
	height: ${({ fitContent, width }) => getPopupHeight(width, fitContent)};
	min-height: ${({ width, fitContent }) => getPopupMinHeight(width, fitContent)};
	max-height: ${({ width, fitContent }) => getPopupMaxHeight(width, fitContent)};
	position: relative;
`;

const StyledPopupContents = styled(Column)<{ width: 440 | 480 | 600 }>`
	width: 100%;
	overflow-y: auto;
	margin-bottom: 20px;
	max-height: ${({ width }) => {
		if (width === 600) {
			return "640px";
		}
	}};
`;

const StyledPopupBottom = styled(Row)`
	margin-top: auto;
	width: 100%;
	padding: 10px 12px;
	border-top: 1px solid ${({ theme }) => theme.colors.neutral_50_20};
`;
